import React, { useState, useEffect, useCallback } from 'react';

import clsx from 'clsx';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import styled from 'styled-components';

import SignatureIcon from 'apps/web_forms/assets/signature-icon.svg';
import SketchIcon from 'apps/web_forms/assets/sketch-question-icon.svg';
import ErrorMessage from 'apps/web_forms/components/commons/ErrorMessage';
import ThumbnailImage from 'apps/web_forms/components/commons/ThumbnailImage';
import useChangeValue from 'apps/web_forms/hooks/useChangeValue';
import useResourceUpdateHighlight from 'apps/web_forms/hooks/useResourceUpdateHighlight';
import { selectFieldErrorById } from 'apps/web_forms/hooks/useGlobalSelector';
import useRules from 'apps/web_forms/hooks/useRules';
import {
  setCurrentSelectedMultimediaInput,
  addImageToSubmit,
  removeImageToSubmit,
  removeImageDraftValue,
} from 'apps/web_forms/slices/multimediaSlice';
import { COLORS, STYLES } from 'apps/web_forms/utils/constants';
import customPropTypes from 'apps/web_forms/utils/customPropTypes';
import { removeDispatchAnswer } from '../../slices/formSlice';

const StyledCanvasInputs = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  &.readOnly {
    background-color: ${COLORS.readOnlyInputBackgroundColor};
    opacity: 0.5;
  }
  label {
    width: 100%;
  }
  .field-container {
    display: flex;
    width: 100%;
  }
  input {
    width: 100%;
    &.canvas-input.invalid {
      border: ${STYLES.ErrorHighlight};
    }
    &.canvas-input.updateHighlight {
      border: ${STYLES.UpdateHighlight};
    }
  }
  .enable-canvas-btn {
    background-color: transparent;
    width: 2em;
    height: 2em;
    border-radius: 0.4em;
    border: none;
    margin-left: 1em;
    font-size: 18px;
    svg {
      width: 2em;
      height: 2em;
    }
  }
  .display-image-container {
    margin-top: 1em;
  }
`;

const CanvasInputs = ({ element, formContext, onChangeValue, toggleActiveValue, showContent }) => {
  const {
    identifier,
    title,
    initialAnswer,
    hint,
    validate_msg: validateMsg,
  } = element;
  const { register } = formContext;

  const { changeValue, currentValue } = useChangeValue(
    element,
    onChangeValue,
    formContext
  );

  const errorMessage = useSelector((state) => selectFieldErrorById(state, identifier));
  const imageToSubmit = useSelector((state) => state.multimedia.imagesToSubmit[identifier]);
  const imageDraftValue = useSelector((state) => state.multimedia.imageDraftValues[identifier]);
  const dispatchAnswer = useSelector((state) => state.form.dispatchAnswers[identifier]);
  const [prepareImageToSubmit, setPrepareImageToSubmit] = useState(undefined);

  const { hasResourceUpdateHighlight, removeResourceUpdateHighlight } =
    useResourceUpdateHighlight({ fieldIdentifier: identifier });

  const dispatch = useDispatch();
  const [{ isActive, isRequired, isElementValid, isReadOnly }] =
    useRules(element, formContext);

  const hasValue = !!imageToSubmit || !!imageDraftValue || !!initialAnswer


  useEffect(() => {
    register(identifier,{
      validate: () => isElementValid || validateMsg || false ,
      required: isRequired && !hasValue ? 'This field is required' : false,
    });
  }, [identifier, isElementValid, validateMsg, isRequired, hasValue]);

  useEffect(() => {
    if (initialAnswer && !imageToSubmit) {
      setPrepareImageToSubmit({
        key: element.identifier,
        value: `data:image/jpeg;base64,${initialAnswer}`,
        isDirty: false,
      })
      dispatch(
        addImageToSubmit({
          key: element.identifier,
          value: `data:image/jpeg;base64,${initialAnswer}`,
        })
      );
      if(imageDraftValue){
        dispatch(removeImageDraftValue(element.identifier));
      }
      if(dispatchAnswer){
        dispatch(removeDispatchAnswer(element.identifier));
      }
      
    }
  }, [initialAnswer]);

  useEffect(() => {
    if (!!imageDraftValue) {
      setPrepareImageToSubmit({
        key: element.identifier,
        value: imageDraftValue.imageUrl,
        isDirty: false,
      })
      dispatch(
        addImageToSubmit({
          key: element.identifier,
          value: imageDraftValue.imageUrl,
        })
      );
      if(imageDraftValue){
        dispatch(removeImageDraftValue(element.identifier));
      }
      if(dispatchAnswer){
        dispatch(removeDispatchAnswer(element.identifier));
      }
    }
  }, [imageDraftValue]);

  useEffect(() => {
    if (prepareImageToSubmit) {
      changeValue(prepareImageToSubmit.value.slice(0, 20), prepareImageToSubmit.isDirty);
    }
  }, [prepareImageToSubmit]);

  useEffect(() => {
    if (!prepareImageToSubmit && imageToSubmit) {
      changeValue(imageToSubmit.slice(0, 20), true);
    }
  }, [imageToSubmit]);

  const handleOpenCanvas = useCallback(() => {
    if (!isReadOnly) {
      dispatch(setCurrentSelectedMultimediaInput({ element }));
    }
  }, [isReadOnly]);

  const handleCloseCanvas = useCallback(() => {
    if (!isReadOnly) {
      dispatch(removeImageToSubmit(identifier));
      if(imageDraftValue){
        dispatch(removeImageDraftValue(identifier));
      }
      if(dispatchAnswer){
        dispatch(removeDispatchAnswer(identifier));
      }
      if(prepareImageToSubmit){
        setPrepareImageToSubmit(undefined);
      }
      changeValue('', true);
    }
  }, [isReadOnly, prepareImageToSubmit]);

  useEffect(() => {
    toggleActiveValue(identifier, isActive);
  }, [isActive]);

  if (!isActive || !showContent) return null;

  const thumbnail =
    imageToSubmit || imageDraftValue?.imageUrl;

  return (
    <div className='react-form-control'>
      <StyledCanvasInputs>
        <label htmlFor={identifier}>{title}</label>
        <input
          name={identifier}
          type='text'
          hidden
        />
        <div className='field-container'>
          <input
            id={identifier}
            type='text'
            onClick={handleOpenCanvas}
            onFocus={removeResourceUpdateHighlight}
            readOnly
            className={clsx(
              'canvas-input',
              errorMessage !== undefined && 'invalid',
              isReadOnly && 'read-only',
              hasResourceUpdateHighlight && 'updateHighlight'
            )}
          />
          <button
            className='enable-canvas-btn'
            type='button'
            aria-label='Enable multimedia canvas'
            onClick={handleOpenCanvas}
            onFocus={removeResourceUpdateHighlight}
          >
            {element.type === 'signature' && <SignatureIcon />}
            {element.type === 'sketch' && <SketchIcon />}
          </button>
        </div>
        {hint && <p className='element-hint'>{hint}</p>}
        {errorMessage !== undefined && (
          <ErrorMessage id={`${identifier}_errmsg`}>
            {errorMessage}
          </ErrorMessage>
        )}
        {thumbnail && (
          <div className='display-image-container'>
            <ThumbnailImage srcValue={thumbnail} onClose={handleCloseCanvas} />
          </div>
        )}
      </StyledCanvasInputs>
    </div>
  );
};

CanvasInputs.propTypes = {
  element: PropTypes.shape({
    title: PropTypes.string,
    identifier: PropTypes.string,
    initialAnswer: PropTypes.string,
    hint: PropTypes.string,
    type: PropTypes.string,
    validate_msg: PropTypes.string,
  }).isRequired,
  formContext: customPropTypes.formContext.isRequired,
  onChangeValue: PropTypes.func.isRequired,
};

export default React.memo(CanvasInputs);
